import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { savePendingAudio, getPendingAudio, deletePendingAudio } from "./audioStore";
import { fetchRecordingSettings, fireLongRecordingReminder, DEFAULT_RECORDING_SETTINGS } from "./recordingReminders";

const RecordingContext = createContext(null);

export const RecordingProvider = ({ children }) => {
  const [session, setSession] = useState(null); // { reportId, patientId, label, language, speakers }
  const [elapsed, setElapsed] = useState(0);
  const [transcribing, setTranscribing] = useState(null); // reportId en cours de transcription
  const [lastResult, setLastResult] = useState(null); // { reportId, report } après sauvegarde auto
  const [pendingVersion, setPendingVersion] = useState(0); // incrémenté quand le stock d'audios en attente change
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const wakeRef = useRef(null);
  const elapsedRef = useRef(0);
  const settingsRef = useRef(DEFAULT_RECORDING_SETTINGS);
  const nextReminderRef = useRef(Infinity); // seconde à laquelle sonner le prochain rappel « séance longue »
  const [overdue, setOverdue] = useState(false);

  const releaseWakeLock = () => { wakeRef.current?.release?.().catch(() => {}); wakeRef.current = null; };

  const transcribeAndSave = useCallback(async (blob, meta) => {
    setTranscribing(meta.reportId);
    try { await savePendingAudio(meta.reportId, blob, meta); } catch { /* stockage local indisponible */ }
    try {
      const fd = new FormData();
      fd.append("audio", blob, "seance.webm");
      fd.append("language", meta.language);
      fd.append("speakers", String(meta.speakers));
      const res = await axios.post(`${API}/session-reports/transcribe-audio`, fd, { withCredentials: true });
      const current = (await axios.get(`${API}/session-reports/patient/${meta.patientId}`, { withCredentials: true })).data.find((r) => r.report_id === meta.reportId);
      const prev = (current?.transcript || "").trim();
      const transcript = prev ? `${prev}\n\n${res.data.transcription}` : res.data.transcription;
      const saved = await axios.put(`${API}/session-reports/${meta.reportId}`, { transcript, language: meta.language, speakers: meta.speakers }, { withCredentials: true });
      try { await deletePendingAudio(meta.reportId); } catch { /* ignore */ }
      setPendingVersion((v) => v + 1);
      setLastResult({ reportId: meta.reportId, report: saved.data, at: Date.now() });
      toast.success(`Transcription terminée et enregistrée (${meta.label})`);
      return true;
    } catch (e) {
      setPendingVersion((v) => v + 1);
      toast.error(`${e.response?.data?.detail || "Erreur de transcription"} — l'audio est conservé sur cet appareil : relancez depuis l'onglet Transcription de la séance.`, { duration: 10000 });
      return false;
    } finally { setTranscribing(null); }
  }, []);

  const retryPending = useCallback(async (reportId) => {
    const item = await getPendingAudio(reportId);
    if (!item) { toast.error("Aucun audio en attente pour cette séance"); return false; }
    return transcribeAndSave(item.blob, item.meta);
  }, [transcribeAndSave]);

  const start = useCallback(async (meta, deviceId) => {
    if (recRef.current) throw new Error("Un enregistrement est déjà en cours");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
    const rec = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm" });
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerRef.current);
      releaseWakeLock();
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      recRef.current = null; streamRef.current = null;
      setSession(null); setElapsed(0); setOverdue(false); nextReminderRef.current = Infinity;
      if (blob.size > 1000) transcribeAndSave(blob, { ...meta, durationSec: elapsedRef.current });
      else toast.info("Enregistrement trop court : rien à transcrire");
    };
    rec.start(1000);
    recRef.current = rec; streamRef.current = stream;
    setSession(meta); setElapsed(0); elapsedRef.current = 0; setOverdue(false);
    fetchRecordingSettings().then((st) => { settingsRef.current = st; nextReminderRef.current = Number(localStorage.getItem("kintsugi_reminder_test_seconds")) || st.reminder_minutes * 60; });
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1; setElapsed(elapsedRef.current);
      if (elapsedRef.current >= nextReminderRef.current) {
        setOverdue(true);
        fireLongRecordingReminder(elapsedRef.current, meta.label, () => recRef.current?.stop());
        nextReminderRef.current = elapsedRef.current + settingsRef.current.repeat_minutes * 60;
      }
    }, 1000);
    try { wakeRef.current = await navigator.wakeLock?.request?.("screen"); } catch { /* non supporté */ }
  }, [transcribeAndSave]);

  const stop = useCallback(() => { recRef.current?.stop(); }, []);

  useEffect(() => {
    const warn = (e) => { if (recRef.current) { e.preventDefault(); e.returnValue = ""; } };
    const onVisible = async () => { if (document.visibilityState === "visible" && recRef.current && !wakeRef.current) { try { wakeRef.current = await navigator.wakeLock?.request?.("screen"); } catch { /* ignore */ } } };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener("beforeunload", warn); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  return <RecordingContext.Provider value={{ session, elapsed, overdue, transcribing, lastResult, pendingVersion, start, stop, retryPending, bumpPending: () => setPendingVersion((v) => v + 1) }}>{children}</RecordingContext.Provider>;
};

export const useRecording = () => useContext(RecordingContext) || { session: null, elapsed: 0, overdue: false, transcribing: null, lastResult: null, pendingVersion: 0, start: async () => {}, stop: () => {}, retryPending: async () => false, bumpPending: () => {} };

export const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
